import store from '@/store';
import { DiscoveryMaterialService } from '@/modules/apiSelector/DiscoveryMaterialService';
import { GetVideoDataService } from '@/modules/apiSelector/GetVideoDataService';
import { GetPdfService } from '@/modules/apiSelector/GetPdfService';
import { GetMaterialService } from './GetMaterialService';
import { GetHtmlService } from '@/modules/apiSelector/GetHtmlService';
import logger from '@/modules/Logger';

/**
 * API Service Registry
 *
 * Each service can define different implementations based on user type:
 * - `new`: Function to execute for canary users
 * - `legacy`: Function to execute for non-canary users
 * - `beta`: Indicates if the API is in beta
 * - `betaAccessLevel`: Minimum value of `student_other_setting.enable_beta` 
 *                      required for access
 */
const API_SERVICES = {
  discoveryMaterial: { ...DiscoveryMaterialService, beta: true, betaAccessLevel: 2 },
  getVideoData:  { ...GetVideoDataService, beta: true, betaAccessLevel: 2 },
  getPdfData: { ...GetPdfService, beta: true, betaAccessLevel: 2 },
  getMaterial: { ...GetMaterialService, beta: true, betaAccessLevel: 2 },
  getHtmlData: { ...GetHtmlService, beta: true, betaAccessLevel: 2 },
};

/**
 * Determines the appropriate API endpoint (legacy or new) based on the user's canary status
 * @param {string} endpoint - API endpoint (e.g., "discoveryMaterial").
 * @returns {Function} - Corresponding API method (new, legacy)
 */
export function getApiMethod(endpoint) {
  let endpointDetail = API_SERVICES[endpoint];
  if (!endpointDetail) {
    return;
  }

  const hasBetaAccess = store.state.enableBeta >= endpointDetail.betaAccessLevel;
  const isCanaryUser = hasBetaAccess && !store.state.isTrial;
  const isNewApi = isCanaryUser || !endpointDetail.beta;

  let logMessage = isNewApi ? 'Using GraphQL' : 'Using Legacy API';
  logger.info(`[Canary Release][${endpoint}] ${logMessage}`, {
    operation: 'lesson:detect-canary-user', 
    enableBeta: store.state.enableBeta, 
    beta: endpointDetail.beta,
    betaAccessLevel: endpointDetail.betaAccessLevel
  }); 

  return isNewApi ? API_SERVICES[endpoint]?.new : API_SERVICES[endpoint]?.legacy;
}
